/**
 * Services Initializer Component
 * Injects persisted last GPS slice into GPS services after persistence boot.
 */

import React, { useEffect, useRef } from "react";
import { rawCompassDataService } from "../services/navigation";
import { filteredGpsDataService } from "../services/navigation/FilteredGpsDataService";
import { gpsPredictionService } from "../services/navigation/GpsPredictionService";
import { rawGpsDataService } from "../services/navigation/RawGpsDataService";
import { lastGpsDataSlice, usePersistedSlice, usePersistenceReady } from "../store/persistence";
import { logAppMemoryReport } from "../tools/AppMemoryProfiler.tools";

/**
 * Component that initializes GPS services after persistence hydration
 * This component should be rendered inside MainProvider
 */
export const ServicesInitializer: React.FC<{ "children": React.ReactNode }> = ( { children } ) => {
    const [ lastGpsData, setLastGpsData ] = usePersistedSlice( lastGpsDataSlice );
    const persistenceReady = usePersistenceReady();
    const initialized = useRef( false );
    useEffect( () => {
        if ( persistenceReady && !initialized.current ) {
            console.log( "🔧 [ServicesInitializer] Initializing GPS services..." );

            rawGpsDataService.initLastValue( lastGpsData );

            const initializeServices = async() => {
                console.log( "🔧 [ServicesInitializer] Injecting persisted GPS slice into GPS services..." );

                // Inject last GPS slice callbacks into all GPS services
                await rawGpsDataService.start( setLastGpsData );

                filteredGpsDataService.start( lastGpsData );

                gpsPredictionService.start();

                await rawCompassDataService.start();

                initialized.current = true;

                console.log( "✅ [ServicesInitializer] Persisted GPS slice injected into all GPS services" );

                logAppMemoryReport( {
                    "context":      "services-initialized",
                    "skipThrottle": true,
                } );
            };

            void  initializeServices();
        }
    }, [ lastGpsData, persistenceReady, setLastGpsData ] );


    return ( <>
{children}
             </> );
};
